import { http, fallback, createConfig, type Config } from 'wagmi';
import { base, baseSepolia } from 'wagmi/chains';
// RainbowKit imports - will be stubbed in B2B mode via Vite alias
import { getDefaultConfig, connectorsForWallets } from '@rainbow-me/rainbowkit';
import {
  walletConnectWallet,
  coinbaseWallet,
} from '@rainbow-me/rainbowkit/wallets';
import { devMode, b2bVersion } from './consts.ts';
import { ENV_CONSTS } from './env-consts.ts';
import { getBaseRpcEndpoints } from './rpc-endpoints.ts';

// In B2B mode, return a minimal stub config (won't be used, but satisfies imports)
let config: Config;

if (b2bVersion) {
  // Stub config for B2B mode - minimal config that won't be used
  // RainbowKit imports are stubbed, so these functions won't be called
  config = createConfig({
    chains: [baseSepolia], // Need at least one chain for type safety
    connectors: [],
    transports: {
      [baseSepolia.id]: http(),
    },
  });
} else {
  // B2C mode - full wagmi setup
  // Define multiple RPC endpoints for Base chain
  const baseRpcEndpoints = getBaseRpcEndpoints();

  // Log the RPC endpoints being used
  console.log('🔗 [RPC] Using endpoints:', baseRpcEndpoints);

  // Create transport with multiple endpoints and fallback - optimized for 429 handling
  const baseTransport = fallback([
    // Primary transport with aggressive retry logic
    http(baseRpcEndpoints[0], {
      retryCount: 1, // Only retry once before falling back
      retryDelay: 100, // Quick retry
    }),
    // Fallback transports with quick switching
    ...baseRpcEndpoints.slice(1).map((endpoint) =>
      http(endpoint, {
        retryCount: 1, // Only retry once before trying next endpoint
        retryDelay: 100, // Quick retry
      })
    ),
  ]);

  const commonProps: Omit<Parameters<typeof getDefaultConfig>[0], 'chains'> = {
    appName: 'CoinRaces',
    projectId: ENV_CONSTS.WALLET_CONNECT_PROJECT_ID,
  };

  const isMobileDevice = () => {
    return window.matchMedia('(pointer: coarse)').matches;
  };

  let devConfig, prodConfig;

  if (isMobileDevice()) {
    const mobileConnectors = connectorsForWallets(
      [
        {
          groupName: 'Mobile',
          wallets: [walletConnectWallet, coinbaseWallet],
        },
      ],
      commonProps
    );

    devConfig = createConfig({
      connectors: mobileConnectors,
      chains: [baseSepolia],
      transports: {
        [baseSepolia.id]: http(),
      },
    });

    prodConfig = createConfig({
      connectors: mobileConnectors,
      chains: [base],
      transports: {
        [base.id]: baseTransport,
      },
    });
  } else {
    devConfig = getDefaultConfig({
      ...commonProps,
      chains: [baseSepolia],
    });

    prodConfig = getDefaultConfig({
      ...commonProps,
      chains: [base],
      transports: {
        [base.id]: baseTransport,
      },
    });
  }

  config = devMode ? devConfig : prodConfig;
}

declare module 'wagmi' {
  // noinspection JSUnusedGlobalSymbols
  interface Register {
    config: typeof config;
  }
}

if (!devMode) {
  const handleDeviceChange = () => {
    window.location.reload();
  };
  window
    .matchMedia('(pointer: coarse)')
    .addEventListener('change', handleDeviceChange);
}

export { config };
